import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import apiService from '@/services/ApiService';
import { CalendarEvent, CalendarStatus } from '@/services/api-providers/CalendarServiceProvider';
import { PositionedEvent, EventsByDay, CalendarDay } from '@/@types/Calendar';
import CalendarEventCard from './CalendarEventCard.vue';
import CalendarEventPopover from './CalendarEventPopover.vue';
import CalendarIntegrationBar from './CalendarIntegrationBar.vue';
import CalendarAllDayRow from './CalendarAllDayRow.vue';
import { useCalendarSync } from '@/composables/useCalendarSync';
import EventBus from '@/EventBus';
import { CALENDAR_DISCONNECTED } from '@/events';
import { dispatchGetMeetingsCount } from '@/store/dispatchers/uiDispatchers';
import {
  HOUR_HEIGHT,
  getWeekStart,
  getWeekDays,
  getWeekLabel,
  formatDateKey,
  eventLocalDate,
  groupEventsByDay,
  assignOverlapColumns,
  formatHour,
  isAllDayEvent,
} from '@/helpers/calendarUtils';

// ---- State ------------------------------------------------------------------

type BotFilter = 'all' | 'bot' | 'unrecorded';


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarView',
  setup(__props) {

const isIntegrationConnected = ref(true); // assume connected until checked
const integrationStatus = ref<CalendarStatus['status']>('connected');
const integrationEmail = ref('');
const currentWeekStart = ref<Date>(getWeekStart(new Date()));
const events = ref<CalendarEvent[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const bodyRef = ref<HTMLElement | null>(null);
const selectedEvent = ref<CalendarEvent | null>(null);
const popoverPosition = ref<{ top: number; left: number }>({ top: 0, left: 0 });

// ---- Filter chips -----------------------------------------------------------

const botFilter = ref<BotFilter>('all');

const filterChips: Array<{ label: string; value: BotFilter }> = [
  { label: 'All events', value: 'all' },
  { label: 'Bot invited/joined', value: 'bot' },
  { label: 'No bot', value: 'unrecorded' },
];

// ---- Computed: week days + label --------------------------------------------

const weekDays = computed<CalendarDay[]>(() => getWeekDays(currentWeekStart.value));

const weekLabel = computed(() => {
  const start = currentWeekStart.value;
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return getWeekLabel(start, end);
});

// ---- P7: Today's upcoming event count ---------------------------------------

const botActiveStatuses = new Set(['inviting', 'invited', 'accepted']);

const todayUpcomingCount = computed(() => {
  const todayKey = formatDateKey(new Date());
  const currentMs = now.value.getTime();

  // Group recurring events as one (same logic as BE getAllMeetingsCountAction)
  const seen = new Set<string>();
  let count = 0;

  for (const ev of events.value) {
    const evDate = eventLocalDate(ev);
    if (evDate !== todayKey) continue;
    // Only count events where bot is invited/active
    if (!botActiveStatuses.has(ev.botInviteStatus)) continue;
    // Include events that haven't ended yet (upcoming or currently happening)
    const endMs = new Date(ev.endTime).getTime();
    if (endMs <= currentMs) continue;

    const groupKey = ev.recurringEventId || ev.providerEventId;
    if (seen.has(groupKey)) continue;
    seen.add(groupKey);
    count++;
  }

  return count;
});

// ---- Reactive now (updates every 60s for now-line) -------------------------

const now = ref(new Date());
let nowTimer: ReturnType<typeof setInterval> | null = null;

// ---- Computed: now line -----------------------------------------------------

const nowLineTop = computed(() => {
  const minutes = now.value.getHours() * 60 + now.value.getMinutes();
  return minutes * (HOUR_HEIGHT / 60);
});

// ---- Computed: events by day ------------------------------------------------

const allDayEvents = computed(() => events.value.filter((ev) => isAllDayEvent(ev)));

const eventsByDay = computed<EventsByDay>(() => {
  const timedEvents = events.value.filter((ev) => !isAllDayEvent(ev));
  const grouped = groupEventsByDay(timedEvents, weekDays.value);

  // Assign overlap columns per day
  for (const dateKey of Object.keys(grouped)) {
    assignOverlapColumns(grouped[dateKey]);
  }

  return grouped;
});

// ---- Methods: event getters for template ------------------------------------

const getTimedEvents = (dateKey: string): PositionedEvent[] => {
  return eventsByDay.value[dateKey] || [];
};

const getFilteredTimedEvents = (dateKey: string): PositionedEvent[] => {
  const dayEvents = getTimedEvents(dateKey);
  if (botFilter.value === 'all') return dayEvents;

  return dayEvents.filter((pe) => {
    const status = pe.event.botInviteStatus || 'none';
    if (botFilter.value === 'bot') {
      return status === 'invited' || status === 'accepted' || status === 'inviting' || status === 'recorded';
    }
    // 'unrecorded'
    return status === 'none' || status === 'failed' || status === 'skipped' || status === 'declined';
  });
};

// ---- Methods: navigation ----------------------------------------------------

const goToPrevWeek = () => {
  const prev = new Date(currentWeekStart.value);
  prev.setDate(prev.getDate() - 7);
  currentWeekStart.value = prev;
};

const goToNextWeek = () => {
  const next = new Date(currentWeekStart.value);
  next.setDate(next.getDate() + 7);
  currentWeekStart.value = next;
};

const goToToday = () => {
  currentWeekStart.value = getWeekStart(new Date());
};

// ---- Methods: event popover -------------------------------------------------

const handleOpenDetail = (eventId: string, clickEvent: MouseEvent) => {
  const found = events.value.find((ev) => ev._id === eventId);
  if (!found) return;

  const rect = (clickEvent.currentTarget as HTMLElement).getBoundingClientRect();
  let left = rect.right + 8;
  let top = rect.top - 20;

  // Overflow right: show to the left of the event card
  if (left + 340 > window.innerWidth) {
    left = rect.left - 340;
  }
  // Overflow bottom: shift up
  if (top + 440 > window.innerHeight) {
    top = window.innerHeight - 460;
  }
  // Overflow top: clamp
  if (top < 8) {
    top = 8;
  }

  popoverPosition.value = { top, left };
  selectedEvent.value = found;
};

const closePopover = () => {
  selectedEvent.value = null;
};

// ---- Real-time sync via WebSocket ------------------------------------------

const updateBotStatus = (eventId: string, status: string, error?: string) => {
  const idx = events.value.findIndex((ev) => ev._id === eventId);
  if (idx !== -1) {
    events.value.splice(idx, 1, {
      ...events.value[idx],
      botInviteStatus: status as CalendarEvent['botInviteStatus'],
      botInviteError: error ?? null,
    });
  }
  if (selectedEvent.value && selectedEvent.value._id === eventId) {
    selectedEvent.value = {
      ...selectedEvent.value,
      botInviteStatus: status as CalendarEvent['botInviteStatus'],
      botInviteError: error ?? null,
    };
  }
};

const {
  lastSyncAt,
  isSyncing,
  syncError,
  connect: connectSync,
  disconnect: disconnectSync,
} = useCalendarSync({
  onEventUpdated: (event: CalendarEvent) => {
    const idx = events.value.findIndex((ev) => ev._id === event._id);
    if (idx !== -1) {
      events.value.splice(idx, 1, { ...events.value[idx], ...event });
    } else {
      // New event -- check if it falls in current week
      const eventDate = eventLocalDate(event);
      const inCurrentWeek = weekDays.value.some((d) => d.dateKey === eventDate);
      if (inCurrentWeek) {
        events.value = [...events.value, event];
      }
    }
    // Update selectedEvent if it matches
    if (selectedEvent.value && selectedEvent.value._id === event._id) {
      selectedEvent.value = { ...selectedEvent.value, ...event };
    }
  },
  onEventDeleted: (providerEventId: string) => {
    const idx = events.value.findIndex((ev) => ev.providerEventId === providerEventId);
    if (idx !== -1) {
      const deletedId = events.value[idx]._id;
      events.value = events.value.filter((ev) => ev.providerEventId !== providerEventId);
      // Close popover if showing deleted event
      if (selectedEvent.value && selectedEvent.value._id === deletedId) {
        selectedEvent.value = null;
      }
    }
  },
  onBotStatusChanged: (payload) => {
    updateBotStatus(payload.eventId, payload.botInviteStatus, payload.botInviteError);
  },
  onSyncError: async () => {
    try {
      const { data } = await apiService.calendar.getStatus();
      if (!data.isConnected) {
        isIntegrationConnected.value = false;
        integrationStatus.value = data.status;
        disconnectSync();
      }
    } catch {
      isIntegrationConnected.value = false;
      integrationStatus.value = 'disconnected';
      disconnectSync();
    }
  },
  onSyncCompleted: () => {
    fetchEvents(true);
  },
});

const relativeLastSync = computed(() => {
  if (!lastSyncAt.value) return '';
  const syncDate = new Date(lastSyncAt.value);
  const diffMs = now.value.getTime() - syncDate.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
});

const handleRefresh = async () => {
  try {
    await apiService.calendar.triggerSync();
  } catch {
    // sync errors handled via WebSocket
  }
  fetchEvents();
};

const handleBotStatusChanged = (payload: { eventId: string; newStatus: string; scope?: string }) => {
  updateBotStatus(payload.eventId, payload.newStatus);
  // Re-fetch all events when a scoped action affected multiple occurrences
  if (payload.scope === 'all') {
    fetchEvents(true);
  }
  // Update sidebar meetings counter
  dispatchGetMeetingsCount();
};

// ---- Integration actions ----------------------------------------------------

const checkIntegration = async () => {
  try {
    const { data } = await apiService.calendar.getStatus();
    isIntegrationConnected.value = data.isConnected;
    integrationStatus.value = data.status;
    integrationEmail.value = data.accountEmail || '';
  } catch {
    isIntegrationConnected.value = false;
    integrationStatus.value = 'disconnected';
  }
};

const handleConnect = async () => {
  try {
    const { data } = await apiService.calendar.connect();
    if (data.authUrl) {
      window.location.href = data.authUrl;
    }
  } catch {
    // IntegrationBar shows connecting state; we leave it as-is
    // The page will reload on redirect anyway
  }
};

const handleDisconnect = async () => {
  try {
    await apiService.calendar.disconnect();
  } catch {
    // Best-effort disconnect
  }
  isIntegrationConnected.value = false;
  integrationStatus.value = 'disconnected';
  integrationEmail.value = '';
  disconnectSync();
  events.value = [];
  selectedEvent.value = null;
  EventBus.$emit(CALENDAR_DISCONNECTED);
};

// ---- Cross-page disconnect handler -----------------------------------------

const onCalendarDisconnected = () => {
  isIntegrationConnected.value = false;
  integrationStatus.value = 'disconnected';
  disconnectSync();
  events.value = [];
  selectedEvent.value = null;
};

// ---- Data fetching ----------------------------------------------------------

const fetchEvents = async (silent = false) => {
  if (!silent) {
    isLoading.value = true;
  }
  hasError.value = false;
  try {
    const start = currentWeekStart.value;
    const end = new Date(start);
    // +7 gives the day AFTER the last visible day (exclusive upper bound).
    // The BE query uses startTime < endDate, so this correctly covers Mon–Sun.
    end.setDate(end.getDate() + 7);

    const startDate = formatDateKey(start);
    const endDate = formatDateKey(end);

    const { data } = await apiService.calendar.getEvents({ startDate, endDate, limit: 500 });
    events.value = data.events;
  } catch {
    hasError.value = true;
    events.value = [];
  } finally {
    isLoading.value = false;
  }
};

// ---- Auto-scroll to current time --------------------------------------------

const scrollToNow = () => {
  const body = bodyRef.value;
  if (!body) return;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const pixelsPerMinute = HOUR_HEIGHT / 60;
  const targetScroll = nowMinutes * pixelsPerMinute - body.clientHeight / 3;
  body.scrollTop = Math.max(0, targetScroll);
};

// ---- Lifecycle --------------------------------------------------------------

onMounted(async () => {
  EventBus.$on(CALENDAR_DISCONNECTED, onCalendarDisconnected);
  await checkIntegration();
  if (isIntegrationConnected.value) {
    await fetchEvents();
    connectSync();
  }
  await nextTick();
  scrollToNow();
  nowTimer = setInterval(() => {
    now.value = new Date();
  }, 60_000);
});

onBeforeUnmount(() => {
  EventBus.$off(CALENDAR_DISCONNECTED, onCalendarDisconnected);
  if (nowTimer) {
    clearInterval(nowTimer);
    nowTimer = null;
  }
  disconnectSync();
});

watch(currentWeekStart, () => {
  fetchEvents();
});

return { __sfc: true,isIntegrationConnected, integrationStatus, integrationEmail, currentWeekStart, events, isLoading, hasError, bodyRef, selectedEvent, popoverPosition, botFilter, filterChips, weekDays, weekLabel, botActiveStatuses, todayUpcomingCount, now, nowTimer, nowLineTop, allDayEvents, eventsByDay, getTimedEvents, getFilteredTimedEvents, goToPrevWeek, goToNextWeek, goToToday, handleOpenDetail, closePopover, updateBotStatus, lastSyncAt, isSyncing, syncError, connectSync, disconnectSync, relativeLastSync, handleRefresh, handleBotStatusChanged, checkIntegration, handleConnect, handleDisconnect, onCalendarDisconnected, fetchEvents, scrollToNow, CalendarEventCard, CalendarEventPopover, CalendarIntegrationBar, CalendarAllDayRow, HOUR_HEIGHT, formatHour }
}

})